import cn from 'classnames';
import type { CSSProperties } from 'react';
import React from 'react';

import s from './typography.module.scss';

export interface ITypographyProps extends React.HTMLAttributes<HTMLElement> {
  'data-test-id'?: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  Tag?: any;
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
}

const Typography: React.FC<ITypographyProps> = props => {
  const { Tag = 'div', className, ...restProps } = props;

  return <Tag className={cn(s.typography, className)} {...restProps} />;
};

Typography.displayName = '_TypographyCommon';

export const Factoid: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography Tag="h1" className={cn(s.factoid, className)} {...restProps} />
  );
};

Factoid.displayName = 'Typography/Factoid';

export const HighlightTitle: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="h1"
      className={cn(s.highlightTitle, className)}
      {...restProps}
    />
  );
};

Factoid.displayName = 'Typography/HighlightTitle';

export const HighlightSubtitle: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="h1"
      className={cn(s.highlightSubtitle, className)}
      {...restProps}
    />
  );
};

Factoid.displayName = 'Typography/HighlightSubtitle';

export const H1: React.FC<ITypographyProps> = ({ className, ...restProps }) => {
  return <Typography Tag="h1" className={cn(s.h1, className)} {...restProps} />;
};

H1.displayName = 'Typography/Headline 1 (H1)';

export const H2: React.FC<ITypographyProps> = ({ className, ...restProps }) => {
  return <Typography Tag="h2" className={cn(s.h2, className)} {...restProps} />;
};

H2.displayName = 'Typography/Headline 2 (H2)';

export const H3: React.FC<ITypographyProps> = ({ className, ...restProps }) => {
  return <Typography Tag="h3" className={cn(s.h3, className)} {...restProps} />;
};

H3.displayName = 'Typography/Headline 3 (H3)';

export const H4: React.FC<ITypographyProps> = ({ className, ...restProps }) => {
  return <Typography Tag="h4" className={cn(s.h4, className)} {...restProps} />;
};

H4.displayName = 'Typography/Headline 4 (H4)';

export const Subtitle: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="span"
      className={cn(s.subtitle, className)}
      {...restProps}
    />
  );
};

Subtitle.displayName = 'Typography/Subtitle (Subtitle)';

export const ButtonM: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="span"
      className={cn(s.button, s.buttonM, className)}
      {...restProps}
    />
  );
};

ButtonM.displayName = 'Typography/ButtonM (ButtonM)';

export const ButtonS: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="span"
      className={cn(s.button, s.buttonS, className)}
      {...restProps}
    />
  );
};

ButtonS.displayName = 'Typography/ButtonS (ButtonS)';

export const BodyL: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="p"
      className={cn(s.body, s.bodyL, className)}
      {...restProps}
    />
  );
};

BodyL.displayName = 'Typography/BodyL (BodyL)';

export const BodyM: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => {
  return (
    <Typography
      Tag="p"
      className={cn(s.body, s.bodyM, className)}
      {...restProps}
    />
  );
};

BodyM.displayName = 'Typography/BodyM (BodyM)';

export const Caption: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => <Typography className={cn(s.caption, className)} {...restProps} />;

Caption.displayName = 'Typography/Caption (Caption)';

export const Subhead: React.FC<ITypographyProps> = ({
  className,
  ...restProps
}) => <Typography className={cn(s.subhead, className)} {...restProps} />;

Subhead.displayName = 'Typography/Subhead (Subhead)';

export const Blockquote: React.FC<
  ITypographyProps & {
    color?: 'snack' | 'warm_1' | 'water' | 'workouts_2' | 'coffee';
    author?: string;
    type?: 'bodyL' | 'bodyM';
  }
> = ({ className, color = 'snack', author, type = 'bodyL', ...restProps }) => {
  return (
    <div className={className}>
      <Typography
        Tag="p"
        style={{ '--color': `var(--${color})` } as CSSProperties}
        className={cn(s.blockquote, s[type])}
        {...restProps}
      />
      {author && (
        <BodyL
          style={{ '--color': `var(--${color})` } as CSSProperties}
          className={cn(s.blockquoteAuthor, s[type])}
        >
          {author}
        </BodyL>
      )}
    </div>
  );
};

export const BulletList: React.FC<ITypographyProps> = ({
  className,
  children,
}) => {
  return <ul className={cn(s.ul, s.typography, className)}>{children}</ul>;
};

export const ListItem: React.FC<
  ITypographyProps & { type?: 'bodyL' | 'bodyM' }
> = ({ className, children, type = 'bodyL' }) => {
  return (
    <li className={cn('mb-3', s[type], s.typography, className)}>{children}</li>
  );
};

// export const T: React.FC<{ children: string }> = props => {
//   const { children } = props;
//
//   return typografFixText(children);
//   // eslint-disable-next-line @typescript-eslint/naming-convention
//   // <span dangerouslySetInnerHTML={{ __html: typografFixText(children) }} />
// };
